<template>
  <div class="container-card">
    <div>
      <img class="logo-otp" src="/assets/images/logos/logo-zd-email.png" alt="logo" />
      <p class="title-otp">
        Ingresa el código de 6 dígitos<br />
        que te enviamos a <span class="email-otp">{{ maskedEmail }}</span>.
      </p>

      <div class="otp-container">
        <input
          :disabled="!email"
          v-for="(digit, index) in 6"
          :key="index"
          type="tel"
          inputmode="numeric"
          pattern="\d*"
          maxlength="1"
          :ref="el => otpRefs[index] = el"
          class="bg-transparent otp-input"
          @input="handleInput($event, index)"
          @keydown.backspace="handleBackspace($event, index)"
          @paste="handlePaste"
        />
      </div>

      <div class="container-btn">
        <button class="btn-resend" :disabled="isResendDisabled || !email" @click="resendCode">
          Reenviar código
          <span v-if="isResendDisabled">({{ formattedTime }})</span>
        </button>

        <button class="btn-back-otp" @click="goBack">
          Regresar
        </button>
      </div>
    </div>

    <div class="footer-otp">
      © {{ year }} Zona Digital
    </div>
  </div>

  <div v-if="showSuccessAnimation" class="animation-overlay">
    <video src="/auht.webm" autoplay muted playsinline width="250"></video>
  </div>
</template>

<script>
import myApi from '@/plugins/axios'
import store from '@/plugins/store'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { getCurrentInstance, ref } from 'vue'

export default {
  name: 'OtpValidate',
  data() {
    return {
      year: new Date().getFullYear(),
      email: this.$route.query.email || '',
      otpCode: '',
      isResendDisabled: false,
      timer: 0,
      intervalId: null,
      showSuccessAnimation: false
    }
  },
  computed: {
    maskedEmail() {
      return this.email.replace(/(.{1}).+(.{1}@.+)/, '$1******$2')
    },
    formattedTime() {
      const minutes = Math.floor(this.timer / 60)
      const seconds = this.timer % 60
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
  },
  setup() {
    const otpRefs = ref([])
    const { proxy } = getCurrentInstance()
    const { executeRecaptcha } = useReCaptcha()

    const handleInput = (e, index) => {
      // forzar 1 dígito numérico
      let v = (e.target.value || '').replace(/\D/g, '')
      if (v.length > 1) v = v[0]
      e.target.value = v

      if (v && index < otpRefs.value.length - 1) {
        otpRefs.value[index + 1]?.focus()
      }
      if (otpRefs.value.every(input => (input?.value || '').length === 1)) {
        const enteredOtp = otpRefs.value.map(i => i.value).join('')
        validateOtp(enteredOtp)
      }
    }

    const handleBackspace = (e, index) => {
      if (!e.target.value && index > 0) {
        otpRefs.value[index - 1]?.focus()
      }
    }
    
    const handlePaste = (e) => {
      e.preventDefault()
      let pastedData = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6)
      pastedData.split('').forEach((char, i) => {
        if (otpRefs.value[i]) otpRefs.value[i].value = char
      })
      const nextIndex = Math.min(pastedData.length, otpRefs.value.length - 1)
      if (otpRefs.value[nextIndex]) otpRefs.value[nextIndex].focus()

      if (otpRefs.value.every(input => (input?.value || '').length === 1)) {
        const enteredOtp = otpRefs.value.map(i => i.value).join('')
        validateOtp(enteredOtp)
      }
    }

    const validateOtp = async (otp) => {
      try {
        const payload = { email: proxy.email, code: otp }
        const resp = await myApi.post('ecommerce/verify-otp', payload)
        
        proxy.$notify({
          title: `${resp?.data?.message}`,
          type: 'success',
          duration: 6000
        })

        proxy.showSuccessAnimation = true

        // 1) Intento de autologin con credenciales guardadas
        const raw = sessionStorage.getItem('pendingLogin')
        let pending = null
        try {
          pending = raw ? JSON.parse(raw) : null
        } catch (e) {
          // JSON inválido: limpiar para no reintentar con datos corruptos
          sessionStorage.removeItem('pendingLogin')
          pending = null
        }

        // Si tu backend devolviera token+user aquí, podrías guardarlos directo:
        // if (resp?.data?.token && resp?.data?.user) {
        //   store.dispatch('saveToken', { token: resp.data.token, user: resp.data.user })
        //   sessionStorage.removeItem('pendingLogin')
        //   setTimeout(() => proxy.$router.push({ name: 'Home' }), 600)
        //   return
        // }

        if (pending?.email && pending?.password) {
          try {
            await store.dispatch('login', {
              email: pending.email,
              password: pending.password,
              CARTS: [],
              executeRecaptcha: executeRecaptcha || null,
            })

            sessionStorage.removeItem('pendingLogin')

            // pequeña espera para mostrar la animación
            setTimeout(() => {
              proxy.$router.push({ name: 'Home' })
            }, 600)
            return
          } catch (e) {
            // si falla autologin, limpia y cae al fallback
            sessionStorage.removeItem('pendingLogin')
          }
        }

        // 2) Fallback si no hay pendingLogin o falló autologin
        setTimeout(() => {
          proxy.$router.push('/login')
        }, 600)

      } catch (error) {
        proxy.$notify({
          title: `${error?.response?.data?.error}`,
          type: 'error',
          duration: 4500
        })
        otpRefs.value.forEach(input => { if (input) input.value = '' })
        if (otpRefs.value[0]) otpRefs.value[0].focus()
      }
    }

    return {
      otpRefs,
      handleInput,
      handleBackspace,
      handlePaste
    }
  },
  mounted() {
    if (!this.email) return
    const resendExpire = localStorage.getItem(`resendExpire_${this.email}`)
    if (resendExpire) {
      const remaining = Math.floor((new Date(resendExpire) - new Date()) / 1000)
      if (remaining > 0) {
        this.startTimer(remaining)
      } else {
        localStorage.removeItem(`resendExpire_${this.email}`)
      }
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    async resendCode() {
      try {
        if (!this.email) return
        const payload = { email: this.email }
        const resp = await myApi.post('ecommerce/request-otp', payload)
        this.$notify({
          title: `${resp?.data?.message}`,
          type: 'success',
          duration: 4500,
        })
        this.startTimer(600)
        const expireTime = new Date()
        expireTime.setSeconds(expireTime.getSeconds() + 600)
        localStorage.setItem(`resendExpire_${this.email}`, expireTime)
      } catch (error) {
        console.log('error', error)
        this.$notify({
          title: `${error?.response?.data?.error}`,
          type: 'error',
          duration: 4500,
        })
      }
    },
    startTimer(duration) {
      this.isResendDisabled = true
      this.timer = duration
      if (this.intervalId) clearInterval(this.intervalId)
      this.intervalId = setInterval(() => {
        if (this.timer > 0) {
          this.timer--
        } else {
          clearInterval(this.intervalId)
          this.isResendDisabled = false
          localStorage.removeItem(`resendExpire_${this.email}`)
        }
      }, 1000)
    }
  },
  beforeUnmount() {
    if (this.intervalId) clearInterval(this.intervalId)
  }
}
</script>

<style>
.animation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
</style>
